import React, { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Button, Spinner, Tooltip } from '@xsolla/ui-core';
import { AlertExclamation } from '@xsolla/ui-icons';

import { useConfig } from '../../../../contexts/config';
import { ChevronMiniIcon, FileIcon } from '../../../ui/icons';
import {
  ChevronStyle,
  NumberChangeStyle,
  NumberStyle,
  NumbersWrapper,
  SpinnerWrapper,
  Title,
  TitleWrapper,
  Wrapper
} from './styleStatisticCard';

type Props = RouteComponentProps & {
  title: string;
  number?: number | null;
  titleLink?: string;
  tooltip?: string;
  numberChange?: number | null;
  fetching: boolean;
  error?: boolean;
  name?: string;
  isAccent?: boolean;
};

const StatisticCard = ({
  title,
  tooltip,
  titleLink,
  number,
  numberChange,
  fetching,
  error = false,
  history,
  name,
  isAccent
}: Props): JSX.Element => {
  const intl = useIntl();
  const { xsollaAnalytics } = useConfig();

  const isHigher = numberChange && numberChange > 0;
  const parsedNumber = number && number >= 1000 ? `${(number / 1000).toFixed(1)}K` : `${number}`;
  let parsedNumberChange: string | null = null;
  if (numberChange && Number.isFinite(numberChange)) {
    if (numberChange < 1 && numberChange > 0) {
      parsedNumberChange = `${Math.abs(numberChange).toFixed(2)}%`;
    } else {
      parsedNumberChange = `${Math.round(Math.abs(numberChange))}%`;
    }
  }

  const onTitleClick = useCallback(() => {
    xsollaAnalytics.sendClick(`login_pa_module__dashboard__${name}_click`, titleLink || null);
    history.push({ pathname: titleLink });
  }, []);

  const renderNumbers = () => (
    <NumbersWrapper>
      <NumberStyle isBrandColor={isAccent}>{parsedNumber}</NumberStyle>
      {parsedNumberChange && (
        <NumberChangeStyle isHigher={!!isHigher}>
          <ChevronStyle>
            <ChevronMiniIcon isHigher={isHigher} />
          </ChevronStyle>
          {parsedNumberChange}
        </NumberChangeStyle>
      )}
    </NumbersWrapper>
  );

  const renderTitle = () => (
    <TitleWrapper>
      <Title isLink={!!titleLink} onClick={onTitleClick}>
        {title}
      </Title>
      {tooltip ? <Tooltip content={tooltip} /> : <FileIcon />}
    </TitleWrapper>
  );

  const renderSpinner = () => (
    <SpinnerWrapper>
      <Spinner />
    </SpinnerWrapper>
  );

  const renderError = () => (
    <SpinnerWrapper>
      <Tooltip content={intl.formatMessage({ id: 'app.error.statistic.tooltip' })}>
        <Button look="icon" palette="brand">
          <AlertExclamation />
        </Button>
      </Tooltip>
    </SpinnerWrapper>
  );

  if (fetching) {
    return (
      <Wrapper>
        {renderTitle()}

        {renderSpinner()}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {renderTitle()}

      {error ? renderError() : renderNumbers()}
    </Wrapper>
  );
};

export default memo(withRouter(StatisticCard));
